import { LoadDescriptionT } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { userModalActions } from 'components/usersModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { presenceActions } from '../actions/presenceActions';
import PresenceOverlay, { type OwnProps } from '../components/PresenceOverlay';

const { presenceSetOthersSimultaneousRinging } = presenceActions;

const hasActivePhoneConversation = (
  loadDescriptions: LoadDescriptionT[],
): boolean =>
  loadDescriptions.some(
    (o) =>
      (o.conversation_channel === 'pstnphone' ||
        o.conversation_channel === 'callback') &&
      o.load_type !== 'acw' &&
      o.load_type !== 'nonlive',
  );

type ExternalProps = Omit<
  OwnProps,
  | 'isSimultaneousRingLoading'
  | 'canMakeInternalCall'
  | 'favorites'
  | 'internalCall'
  | 'showUserModal'
  | 'presenceSetOthersSimultaneousRinging'
  | 'personalAddToFavouriteTeamMemberIds'
  | 'personalRemoveFromFavouriteTeamMemberIds'
>;

const PresenceOverlayContainer = (props: ExternalProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { addFavorite, removeFavorite, favoriteAgentIds } = useFavoriteAgents();

  const isSimultaneousRingLoading = useAppSelector(
    (state) => state.presence.meta.toggleSimultaneousRing === 'loading',
  );

  const canMakeInternalCall = useAppSelector(
    (state) =>
      !state.telephony.isMicrophoneBlocked &&
      !hasActivePhoneConversation(
        state.presence.presence.presence_status.load_descriptions,
      ),
  );

  const internalCall = useCallback(
    (payload: any) =>
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )(payload),
    [dispatch],
  );

  const showUserModal = useCallback(
    (payload: { user: Partial<User> }) =>
      dispatch(userModalActions.show(payload)),
    [dispatch],
  );

  const presenceSetOthersSimultaneousRingingFn = useCallback(
    (payload: any) =>
      bindActionToPromise(
        dispatch,
        presenceSetOthersSimultaneousRinging,
      )(payload),
    [dispatch],
  );

  const personalAddToFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) => {
      addFavorite(payload.agentId);
      return Promise.resolve();
    },
    [addFavorite],
  );

  const personalRemoveFromFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) => {
      removeFavorite(payload.agentId);
      return Promise.resolve();
    },
    [removeFavorite],
  );

  return (
    <PresenceOverlay
      {...props}
      isSimultaneousRingLoading={isSimultaneousRingLoading}
      canMakeInternalCall={canMakeInternalCall}
      favorites={favoriteAgentIds}
      internalCall={internalCall}
      showUserModal={showUserModal}
      presenceSetOthersSimultaneousRinging={
        presenceSetOthersSimultaneousRingingFn
      }
      personalAddToFavouriteTeamMemberIds={personalAddToFavouriteTeamMemberIds}
      personalRemoveFromFavouriteTeamMemberIds={
        personalRemoveFromFavouriteTeamMemberIds
      }
    />
  );
};

export default PresenceOverlayContainer;
